"use client";

import { InfoTooltip } from "@/app/Components/InfoTooltip";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import type { EventFilters, EventSource } from "@/app/types/events";
import { EVENT_SOURCE_LABELS } from "@/app/types/events";

type EventSidebarProps = {
  filters: EventFilters;
  setFilters: (
    next: EventFilters | ((previous: EventFilters) => EventFilters),
  ) => void;
  sources: EventSource[];
};

export default function EventSidebar({
  filters,
  setFilters,
  sources,
}: EventSidebarProps) {
  const toggleSource = (source: EventSource) => {
    setFilters((previous) => {
      // The API treats an empty source array as all sources, never no results.
      const selected = previous.sources.length ? previous.sources : sources;
      if (selected.length === 1 && selected.includes(source)) return previous;
      return { ...previous, sources: selected.includes(source)
        ? selected.filter((item) => item !== source)
        : [...selected, source] };
    });
  };

  return (
    <div>
      <fieldset>
        <legend className="flex items-center gap-2 text-sm font-semibold text-content">
          Sources
          <InfoTooltip title="Source filters" text="Choose one or more calendars. Sports starts with Athletics and Recreation. Select IMLeagues to include intramural facility times; some records omit sport and team names. Results update immediately, and at least one source stays selected." />
        </legend>
        <div className="mt-2 grid gap-1">
          {sources.map((source) => {
            const effectiveSources = filters.sources.length ? filters.sources : sources;
            const selected = effectiveSources.includes(source);
            return (
              <button
                key={source}
                type="button"
                role="checkbox"
                aria-checked={selected}
                disabled={selected && effectiveSources.length === 1}
                onClick={() => toggleSource(source)}
                className="flex min-h-12 w-full items-center gap-3 rounded-control px-3 text-left text-sm text-content hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <CheckCircleIcon aria-hidden="true" className={`h-5 w-5 shrink-0 ${selected ? "text-focus" : "text-content-muted opacity-30"}`} />
                {source === "imleagues" ? "Intramural facility times (IMLeagues)" : EVENT_SOURCE_LABELS[source] ?? source}
              </button>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}
